import type { AdminLocale } from '../content/locale.js';
import { ApiError } from './http.js';

/* Mapeia os códigos de erro das 3 escritas de config-service (domínios públicos, config de
   moderação, slugs reservados) para mensagens acionáveis. Antes, qualquer falha virava um toast
   genérico ("Não foi possível salvar…"), escondendo a causa — inclusive o caso comum de
   CONFIG_ADMIN_TOKEN ausente no admin-service (503 config_service_unconfigured).

   `locale` é OPCIONAL (default pt-BR) e não um `Bilingual<T>`/`useBilingual`, seguindo o padrão
   já estabelecido nos irmãos (`releases-error.ts`, `legal-error.ts`): estas são funções puras,
   chamadas de dentro de `catch` e de call sites que não são componentes — não há hook ali. Quem
   é componente passa `useAdminLocale().locale`; o default mantém os call sites antigos
   compilando sem mudança.

   Um `switch` (em vez de Record) evita chaves snake_case no objeto (useNamingConvention)
   mantendo os códigos crus do backend. */
function messageForServerCode(serverCode: string, locale: AdminLocale): string | undefined {
  const pt = locale === 'pt-BR';
  switch (serverCode) {
    case 'config_service_unconfigured':
      return pt
        ? 'Serviço de configuração indisponível: o token de escrita (CONFIG_ADMIN_TOKEN) não está provisionado no admin-service. Avise a operação.'
        : 'Configuration service unavailable: the write token (CONFIG_ADMIN_TOKEN) is not provisioned on admin-service. Let the operations team know.';
    case 'config_read_failed':
      return pt
        ? 'Não foi possível ler a configuração atual do serviço de configuração.'
        : 'Could not read the current settings from the configuration service.';
    case 'config_write_failed':
      return pt
        ? 'Falha ao gravar no serviço de configuração — token inválido ou serviço fora do ar.'
        : 'Failed to write to the configuration service — invalid token or the service is down.';
    case 'config_read_error':
      return pt
        ? 'Erro ao ler o serviço de configuração. Tente novamente.'
        : 'Error reading the configuration service. Try again.';
    case 'config_write_error':
      return pt
        ? 'Erro ao gravar no serviço de configuração. Tente novamente.'
        : 'Error writing to the configuration service. Try again.';
    default:
      return undefined;
  }
}

/** Resolve a melhor mensagem para uma falha de escrita de config-service. */
export function configServiceErrorMessage(
  err: unknown,
  fallback: string,
  locale: AdminLocale = 'pt-BR',
): string {
  const pt = locale === 'pt-BR';
  if (err instanceof ApiError) {
    const mapped = messageForServerCode(err.serverCode, locale);
    if (mapped !== undefined) return mapped;
    if (err.status === 503) {
      return pt
        ? 'Serviço de configuração indisponível no momento.'
        : 'The configuration service is unavailable right now.';
    }
    if (err.status === 403) {
      return pt
        ? 'Você não tem permissão para alterar esta configuração.'
        : 'You do not have permission to change this setting.';
    }
    if (err.status >= 500) {
      return pt
        ? 'Falha no serviço de configuração. Tente novamente.'
        : 'The configuration service failed. Try again.';
    }
  }
  return fallback;
}
